/**
 * Motor de deteccion de "leads no gestionados".
 *
 * Un lead se considera NO GESTIONADO cuando cumple al menos uno de los
 * criterios activos. Cada criterio es independiente y configurable, y un mismo
 * lead puede acumular varios (eso eleva su severidad).
 */

export const NO_PIPELINE = '__sin_pipeline__'

export const CRITERIA = {
  SIN_OPORTUNIDAD: {
    id: 'SIN_OPORTUNIDAD',
    label: 'Sin oportunidad',
    short: 'Sin opp.',
    color: '#d92d20',
    help: 'Contacto con antiguedad suficiente que nunca entro en un pipeline.',
  },
  ETAPA_ESTANCADA: {
    id: 'ETAPA_ESTANCADA',
    label: 'Estancado en etapa',
    short: 'Estancado',
    color: '#b54708',
    help: 'Oportunidad abierta que no cambia de etapa desde hace demasiado tiempo.',
  },
  SIN_ACTIVIDAD: {
    id: 'SIN_ACTIVIDAD',
    label: 'Sin actividad',
    short: 'Inactivo',
    color: '#9f1ab1',
    help: 'Ni la oportunidad ni el contacto registran movimiento reciente.',
  },
  NUNCA_AVANZO: {
    id: 'NUNCA_AVANZO',
    label: 'Nunca avanzo de la primera etapa',
    short: 'Sin avance',
    color: '#c11574',
    help: 'Oportunidad que sigue en la etapa inicial del pipeline desde su creacion.',
  },
  SIN_ASIGNAR: {
    id: 'SIN_ASIGNAR',
    label: 'Sin responsable',
    short: 'Sin dueno',
    color: '#175cd3',
    help: 'Oportunidad abierta sin usuario asignado: nadie la esta trabajando.',
  },
}

export const DEFAULT_SETTINGS = {
  /** Dias sin cambiar de etapa para considerar la oportunidad estancada. */
  stagnantDays: 14,
  /** Dias sin ninguna actualizacion para considerar el lead inactivo. */
  noActivityDays: 30,
  /** Dias en la etapa inicial sin avanzar nunca. */
  firstStageDays: 7,
  /** Antiguedad minima del contacto para exigirle una oportunidad. */
  contactGraceDays: 2,
  /** Solo se evaluan oportunidades con estos estados. */
  statuses: ['open'],
  /** Criterios activos. */
  enabled: {
    SIN_OPORTUNIDAD: true,
    ETAPA_ESTANCADA: true,
    SIN_ACTIVIDAD: true,
    NUNCA_AVANZO: true,
    SIN_ASIGNAR: true,
  },
  /** Umbrales por pipeline: { [pipelineId]: { stagnantDays, ... } } */
  perPipeline: {},
}

const DAY = 86400000

export function daysSince(value, now = Date.now()) {
  const t = toTime(value)
  if (t === null) return null
  return Math.max(0, Math.floor((now - t) / DAY))
}

function toTime(value) {
  if (!value) return null
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value
  const t = Date.parse(value)
  return Number.isNaN(t) ? null : t
}

/** Combina los umbrales globales con la sobreescritura del pipeline. */
export function thresholdsFor(settings, pipelineId) {
  return { ...settings, ...(settings.perPipeline?.[pipelineId] || {}) }
}

/* -------------------------------------------------------------------------- */
/* Normalizacion: la API devuelve nombres distintos segun endpoint y version.  */
/* No se conserva el registro original: en subcuentas de decenas de miles de   */
/* fichas duplicaria la memoria y la cache, y nada del informe lo usa.        */
/* -------------------------------------------------------------------------- */

export function normalizeOpportunity(raw) {
  const contact = raw.contact || {}
  return {
    id: raw.id || raw._id,
    name: raw.name || contact.name || 'Sin nombre',
    pipelineId: raw.pipelineId || raw.pipeline_id,
    stageId: raw.pipelineStageId || raw.stageId || raw.pipeline_stage_id,
    status: (raw.status || '').toLowerCase(),
    monetaryValue: Number(raw.monetaryValue ?? raw.monetary_value ?? 0) || 0,
    assignedTo: raw.assignedTo || raw.assigned_to || null,
    contactId: raw.contactId || raw.contact_id || contact.id || null,
    contactName: contact.name || [contact.firstName, contact.lastName].filter(Boolean).join(' ') || null,
    contactEmail: contact.email || null,
    contactPhone: contact.phone || null,
    source: raw.source || null,
    createdAt: raw.createdAt || raw.dateAdded || raw.created_at || null,
    updatedAt: raw.updatedAt || raw.dateUpdated || raw.updated_at || null,
    lastStageChangeAt: raw.lastStageChangeAt || raw.last_stage_change_at || null,
    lastStatusChangeAt: raw.lastStatusChangeAt || raw.last_status_change_at || null,
  }
}

export function normalizeContact(raw) {
  const name = raw.contactName || raw.name ||
    [raw.firstName || raw.firstNameLowerCase, raw.lastName || raw.lastNameLowerCase].filter(Boolean).join(' ')
  return {
    id: raw.id || raw._id,
    name: (name || '').trim() || 'Sin nombre',
    email: raw.email || null,
    phone: raw.phone || null,
    source: raw.source || raw.attributionSource?.utmSource || null,
    tags: raw.tags || [],
    assignedTo: raw.assignedTo || null,
    createdAt: raw.dateAdded || raw.createdAt || null,
    updatedAt: raw.dateUpdated || raw.updatedAt || null,
    lastActivity: raw.lastActivity || raw.dateUpdated || null,
  }
}

/* -------------------------------------------------------------------------- */
/* Evaluacion                                                                  */
/* -------------------------------------------------------------------------- */

/** Devuelve las razones por las que una oportunidad esta sin gestionar. */
export function evaluateOpportunity(opp, { pipeline, settings, now }) {
  const t = thresholdsFor(settings, opp.pipelineId)
  const reasons = []
  if (!t.statuses.includes(opp.status)) return reasons

  const stageAge = daysSince(opp.lastStageChangeAt || opp.createdAt, now)
  const activityAge = daysSince(opp.updatedAt || opp.createdAt, now)
  const oppAge = daysSince(opp.createdAt, now)
  const firstStageId = pipeline?.stages?.[0]?.id

  if (t.enabled.ETAPA_ESTANCADA && stageAge !== null && stageAge >= t.stagnantDays) {
    reasons.push(reason('ETAPA_ESTANCADA', `${stageAge} dias en la misma etapa (umbral ${t.stagnantDays})`, stageAge))
  }

  if (t.enabled.SIN_ACTIVIDAD && activityAge !== null && activityAge >= t.noActivityDays) {
    reasons.push(reason('SIN_ACTIVIDAD', `${activityAge} dias sin ninguna actualizacion (umbral ${t.noActivityDays})`, activityAge))
  }

  if (
    t.enabled.NUNCA_AVANZO && firstStageId && opp.stageId === firstStageId &&
    oppAge !== null && oppAge >= t.firstStageDays
  ) {
    reasons.push(reason('NUNCA_AVANZO', `Creada hace ${oppAge} dias y sigue en "${pipeline.stages[0].name}"`, oppAge))
  }

  if (t.enabled.SIN_ASIGNAR && !opp.assignedTo) {
    reasons.push(reason('SIN_ASIGNAR', 'La oportunidad no tiene usuario asignado', oppAge))
  }

  return reasons
}

function reason(id, detail, days) {
  return { id, label: CRITERIA[id].label, detail, days: days ?? null }
}

/** critico: 3+ razones o 3x el umbral; alto: 2 razones o 2x; medio: el resto. */
export function severityOf(reasons, worstDays, threshold) {
  if (reasons.length >= 3 || (worstDays !== null && threshold && worstDays >= threshold * 3)) return 'critico'
  if (reasons.length === 2 || (worstDays !== null && threshold && worstDays >= threshold * 2)) return 'alto'
  return 'medio'
}

/* -------------------------------------------------------------------------- */
/* Construccion del informe                                                    */
/* -------------------------------------------------------------------------- */

export function buildReport({ contacts, opportunities, pipelines, settings, now = Date.now() }) {
  const pipelineById = new Map(pipelines.map((p) => [p.id, p]))
  const stageName = new Map()
  for (const p of pipelines) for (const s of p.stages || []) stageName.set(s.id, s.name)

  const oppsByContact = new Map()
  for (const o of opportunities) {
    if (!o.contactId) continue
    if (!oppsByContact.has(o.contactId)) oppsByContact.set(o.contactId, [])
    oppsByContact.get(o.contactId).push(o)
  }

  const leads = []

  // --- Criterio 1: contactos sin ninguna oportunidad ------------------------
  if (settings.enabled.SIN_OPORTUNIDAD) {
    for (const c of contacts) {
      if (oppsByContact.has(c.id)) continue
      const age = daysSince(c.createdAt, now)
      if (age !== null && age < settings.contactGraceDays) continue
      const inactivity = daysSince(c.lastActivity || c.updatedAt || c.createdAt, now)
      const reasons = [reason(
        'SIN_OPORTUNIDAD',
        `Contacto creado hace ${age ?? '?'} dias sin oportunidad en ningun pipeline`,
        age,
      )]
      if (settings.enabled.SIN_ACTIVIDAD && inactivity !== null && inactivity >= settings.noActivityDays) {
        reasons.push(reason('SIN_ACTIVIDAD', `${inactivity} dias sin actividad registrada`, inactivity))
      }
      leads.push({
        key: `c:${c.id}`,
        kind: 'contacto',
        contactId: c.id,
        opportunityId: null,
        name: c.name,
        email: c.email,
        phone: c.phone,
        source: c.source,
        assignedTo: c.assignedTo,
        value: 0,
        pipelineId: NO_PIPELINE,
        pipelineName: 'Sin pipeline',
        stageId: null,
        stageName: '-',
        status: 'sin oportunidad',
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
        daysStuck: age,
        daysInactive: inactivity,
        reasons,
        severity: severityOf(reasons, age, settings.contactGraceDays * 7),
      })
    }
  }

  // --- Criterios 2..5: oportunidades ---------------------------------------
  for (const o of opportunities) {
    const pipeline = pipelineById.get(o.pipelineId)
    const reasons = evaluateOpportunity(o, { pipeline, settings, now })
    if (!reasons.length) continue

    const t = thresholdsFor(settings, o.pipelineId)
    const stageAge = daysSince(o.lastStageChangeAt || o.createdAt, now)
    const inactivity = daysSince(o.updatedAt || o.createdAt, now)
    const worst = Math.max(stageAge ?? 0, inactivity ?? 0)

    leads.push({
      key: `o:${o.id}`,
      kind: 'oportunidad',
      contactId: o.contactId,
      opportunityId: o.id,
      name: o.contactName || o.name,
      email: o.contactEmail,
      phone: o.contactPhone,
      source: o.source,
      assignedTo: o.assignedTo,
      value: o.monetaryValue,
      pipelineId: o.pipelineId,
      pipelineName: pipeline?.name || 'Pipeline desconocido',
      stageId: o.stageId,
      stageName: stageName.get(o.stageId) || '-',
      status: o.status,
      createdAt: o.createdAt,
      updatedAt: o.updatedAt,
      daysStuck: stageAge,
      daysInactive: inactivity,
      reasons,
      severity: severityOf(reasons, worst, t.stagnantDays),
    })
  }

  leads.sort((a, b) => (b.daysStuck ?? 0) - (a.daysStuck ?? 0) || b.reasons.length - a.reasons.length)

  return {
    generatedAt: new Date(now).toISOString(),
    leads,
    pipelines: buildPipelineGroups({ leads, pipelines, opportunities, contacts, settings, stageName }),
    totals: buildTotals({ leads, opportunities, contacts, settings }),
  }
}

function buildPipelineGroups({ leads, pipelines, opportunities, contacts, settings, stageName }) {
  const groups = []
  const oppsByPipeline = new Map()
  for (const o of opportunities) {
    if (!settings.statuses.includes(o.status)) continue
    if (!oppsByPipeline.has(o.pipelineId)) oppsByPipeline.set(o.pipelineId, [])
    oppsByPipeline.get(o.pipelineId).push(o)
  }
  const leadsByPipeline = new Map()
  for (const l of leads) {
    if (!leadsByPipeline.has(l.pipelineId)) leadsByPipeline.set(l.pipelineId, [])
    leadsByPipeline.get(l.pipelineId).push(l)
  }

  for (const p of pipelines) {
    groups.push(makeGroup({
      id: p.id,
      name: p.name,
      stages: p.stages || [],
      totalItems: (oppsByPipeline.get(p.id) || []).length,
      leads: leadsByPipeline.get(p.id) || [],
      stageName,
    }))
  }

  const orphan = leadsByPipeline.get(NO_PIPELINE) || []
  if (orphan.length) {
    groups.push(makeGroup({
      id: NO_PIPELINE,
      name: 'Sin pipeline - contactos sin oportunidad',
      stages: [],
      totalItems: contacts.length,
      leads: orphan,
      stageName,
    }))
  }

  return groups.sort((a, b) => b.unmanaged - a.unmanaged)
}

function makeGroup({ id, name, stages, totalItems, leads, stageName }) {
  const byReason = {}
  for (const key of Object.keys(CRITERIA)) byReason[key] = 0
  const bySeverity = { critico: 0, alto: 0, medio: 0 }
  const byStage = new Map()
  let value = 0
  let daysSum = 0
  let daysCount = 0

  for (const l of leads) {
    for (const r of l.reasons) byReason[r.id] = (byReason[r.id] || 0) + 1
    bySeverity[l.severity] += 1
    value += l.value || 0
    if (l.daysStuck !== null) { daysSum += l.daysStuck; daysCount += 1 }
    const sid = l.stageId || '-'
    const cur = byStage.get(sid) || { id: sid, name: stageName.get(sid) || l.stageName, count: 0, value: 0 }
    cur.count += 1
    cur.value += l.value || 0
    byStage.set(sid, cur)
  }

  const orderedStages = stages.length
    ? stages.map((s) => byStage.get(s.id) || { id: s.id, name: s.name, count: 0, value: 0 })
    : [...byStage.values()]

  return {
    id,
    name,
    totalItems,
    unmanaged: leads.length,
    rate: totalItems ? leads.length / totalItems : 0,
    value,
    avgDaysStuck: daysCount ? Math.round(daysSum / daysCount) : 0,
    maxDaysStuck: leads.reduce((m, l) => Math.max(m, l.daysStuck ?? 0), 0),
    byReason,
    bySeverity,
    stages: orderedStages,
    leads,
  }
}

function buildTotals({ leads, opportunities, contacts, settings }) {
  const openOpps = opportunities.filter((o) => settings.statuses.includes(o.status))
  const byReason = {}
  for (const key of Object.keys(CRITERIA)) byReason[key] = 0
  const bySeverity = { critico: 0, alto: 0, medio: 0 }
  for (const l of leads) {
    for (const r of l.reasons) byReason[r.id] += 1
    bySeverity[l.severity] += 1
  }
  const uniqueContacts = new Set(leads.map((l) => l.contactId).filter(Boolean)).size

  return {
    contacts: contacts.length,
    openOpportunities: openOpps.length,
    unmanaged: leads.length,
    unmanagedContacts: uniqueContacts,
    valueAtRisk: leads.reduce((s, l) => s + (l.value || 0), 0),
    rate: openOpps.length + contacts.length ? leads.length / (openOpps.length + contacts.length) : 0,
    avgDaysStuck: leads.length
      ? Math.round(leads.reduce((s, l) => s + (l.daysStuck ?? 0), 0) / leads.length)
      : 0,
    byReason,
    bySeverity,
  }
}
